import React, { useState } from "react";
import DropZone from "../components/DropZone";
import PageContainer from "../components/PageContainer";
import { storage, db } from "../firebase";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL
} from "firebase/storage";
import {
  doc,
  collection,
  setDoc,
  updateDoc,
  serverTimestamp,
  arrayUnion
} from "firebase/firestore";
import { useAuth } from "../context/AuthContext";

function FormPage() {
  const { currentUser } = useAuth();

  // File state
  const [dryStems, setDryStems] = useState([]);
  const [wetStems, setWetStems] = useState([]);
  const [finalMaster, setFinalMaster] = useState([]);

  // Form fields
  const [genre, setGenre] = useState("");
  const [adjectives, setAdjectives] = useState("");

  // Upload UI state
  const [isUploading, setIsUploading] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);

  // Track each file's current bytesTransferred
  const [perFileTransferred, setPerFileTransferred] = useState([]);

  const MAX_RETRIES = 3;

  const sanitizeFileName = (filename) =>
    filename.replace(/\s+/g, "_").replace(/[^\w.\-()]/g, "");

  /**
   * Upload a single file with optional retries. We pass in:
   * - fileIndex: so we know which entry in perFileTransferred to update
   * - totalSize: total bytes of all files (used in calculating overall progress)
   */
  const uploadFileWithRetry = (file, folderPath, fileIndex, totalSize, retries = MAX_RETRIES) =>
    new Promise((resolve, reject) => {
      const safeName = sanitizeFileName(file.name);
      const filePath = `${folderPath}/${Date.now()}-${safeName}`;
      const storageRef = ref(storage, filePath);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          // Snapshot has .bytesTransferred for THIS file
          const currentTransferred = snapshot.bytesTransferred;

          // Update perFileTransferred[fileIndex] = currentTransferred
          setPerFileTransferred((prev) => {
            const newArray = [...prev];
            newArray[fileIndex] = currentTransferred;

            // Sum across all files
            const totalUploadedSoFar = newArray.reduce((acc, val) => acc + val, 0);

            // Convert to a percentage
            let newProgress = 0;
            if (totalSize > 0) {
              newProgress = (totalUploadedSoFar / totalSize) * 100;
            }
            setOverallProgress(newProgress);

            return newArray;
          });
        },
        (error) => {
          // Retry logic
          if (retries > 0) {
            console.warn(`Retrying upload for ${file.name} (Attempt #${MAX_RETRIES - retries + 1})`);
            setTimeout(() => {
              uploadFileWithRetry(file, folderPath, fileIndex, totalSize, retries - 1)
                .then(resolve)
                .catch(reject);
            }, 2000);
          } else {
            console.error(`Failed to upload ${file.name} after ${MAX_RETRIES} retries.`);
            reject(error);
          }
        },
        async () => {
          // Success -> getDownloadURL
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve({
            fileName: file.name,
            storagePath: storageRef.fullPath,
            downloadURL
          });
        }
      );
    });

  /**
   * Upload an array of files at groupPath. We offset fileIndex by startIndex
   * so each file's progress is tracked in perFileTransferred properly.
   */
  const uploadFileGroup = (files, groupPath, startIndex, totalSize) => {
    const promises = files.map((file, i) =>
      uploadFileWithRetry(file, groupPath, startIndex + i, totalSize)
    );
    return Promise.all(promises);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      alert("You must be logged in to submit!");
      return;
    }

    const allFiles = [...dryStems, ...wetStems, ...finalMaster];
    if (allFiles.length === 0) {
      alert("Please select files to upload first.");
      return;
    }

    if (!genre.trim() || !adjectives.trim()) {
      alert("Please fill out Genre and Adjectives fields.");
      return;
    }

    setIsUploading(true);
    setOverallProgress(0);

    // Instead of storing total in state, use a local variable:
    const totalSize = allFiles.reduce((acc, file) => acc + file.size, 0);
    if (totalSize === 0) {
      alert("No file sizes found!");
      setIsUploading(false);
      return;
    }

    // Initialize perFileTransferred
    setPerFileTransferred(new Array(allFiles.length).fill(0));

    try {
      // 1) Create Firestore doc
      const submissionRef = doc(collection(db, "form_submissions"));
      const submissionId = submissionRef.id;

      const engineerUid = currentUser.uid;
      const dryPath = `form_submissions/${engineerUid}/${submissionId}/dryStems`;
      const wetPath = `form_submissions/${engineerUid}/${submissionId}/wetStems`;
      const masterPath = `form_submissions/${engineerUid}/${submissionId}/finalMaster`;

      // 2) Upload each group in sequence so we can pass the correct startIndex
      let startIndex = 0;
      const dryStemsData = await uploadFileGroup(dryStems, dryPath, startIndex, totalSize);
      startIndex += dryStems.length;

      const wetStemsData = await uploadFileGroup(wetStems, wetPath, startIndex, totalSize);
      startIndex += wetStems.length;

      const finalMasterData = await uploadFileGroup(finalMaster, masterPath, startIndex, totalSize);

      // 3) Save doc
      const submissionData = {
        engineerId: engineerUid,
        createdAt: serverTimestamp(),
        genre,
        adjectives,
        dryStems: dryStemsData,
        wetStems: wetStemsData,
        finalMaster: finalMasterData
      };
      await setDoc(submissionRef, submissionData);

      // 4) Update user doc
      const userRef = doc(db, "users", engineerUid);
      await updateDoc(userRef, {
        submissions: arrayUnion(submissionId)
      });

      alert("Submission data saved to Firestore, and files uploaded!");

      // 5) Reset
      setDryStems([]);
      setWetStems([]);
      setFinalMaster([]);
      setGenre("");
      setAdjectives("");
      setOverallProgress(100);

    } catch (error) {
      console.error("Upload error:", error);
      alert("There was an error uploading your files. Check console for details.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <PageContainer>
      <h2>Upload Your Tracks</h2>
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "1.5rem",
        }}
      >
        {/* Drop Zones */}
        <div style={{ width: "100%" }}>
          <label>Dry Stems (folder):</label>
          <DropZone
            label="Upload Dry Stems"
            onFilesAdded={setDryStems}
            allowFolders={true}
          />

          <label>Wet Stems (folder):</label>
          <DropZone
            label="Upload Wet Stems"
            onFilesAdded={setWetStems}
            allowFolders={true}
          />

          <label>Final Master Track:</label>
          <DropZone
            label="Upload Final Master"
            onFilesAdded={setFinalMaster}
            allowFolders={false}
          />
        </div>

        {/* Genre Field */}
        <div style={{ display: "flex", flexDirection: "column", width: "100%", maxWidth: "400px" }}>
          <label htmlFor="genre">Genre of music:</label>
          <input
            type="text"
            id="genre"
            value={genre}
            onChange={(e) => setGenre(e.target.value)}
            placeholder="e.g. Pop, Rock, Hip-Hop..."
            style={{
              backgroundColor: "#333",
              color: "#fff",
              borderRadius: "8px",
              border: "1px solid #555",
              padding: "0.5rem"
            }}
          />
        </div>

        {/* Adjectives Field */}
        <div style={{ display: "flex", flexDirection: "column", width: "100%", maxWidth: "400px" }}>
          <label htmlFor="adjectives">Adjectives to describe the mixing style:</label>
          <input
            type="text"
            id="adjectives"
            value={adjectives}
            onChange={(e) => setAdjectives(e.target.value)}
            placeholder="e.g. Warm, Clear, Saturated..."
            style={{
              backgroundColor: "#333",
              color: "#fff",
              borderRadius: "8px",
              border: "1px solid #555",
              padding: "0.5rem"
            }}
          />
        </div>

        {/* Progress Bar */}
        {isUploading && (
          <div style={{ width: "100%", maxWidth: "400px" }}>
            <div
              style={{
                background: "#333", // Matches the dark gray of the text fields
                height: "10px",
                width: "100%",
                borderRadius: "8px", // Rounded corners for consistency
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: "#76c7c0", // Lighter green for the progress bar
                  height: "10px",
                  width: `${overallProgress}%`,
                  transition: "width 0.2s ease",
                }}
              />
            </div>
            <p style={{ textAlign: "right", marginTop: "4px", color: "#fff" }}>
              {Number.isFinite(overallProgress)
                ? `${Math.round(overallProgress)}% uploaded`
                : "Calculating..."}
            </p>
          </div>
        )}



        {/* Submit Button */}
        <button
          type="submit"
          style={{
            padding: "0.75rem 1.5rem",
            backgroundColor: "#333",
            color: "#fff",
            borderRadius: "8px",
            border: "1px solid #555"
          }}
          disabled={isUploading}
        >
          {isUploading ? "Uploading..." : "Submit"}
        </button>
      </form>
    </PageContainer>
  );
}

export default FormPage;
